import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Alert, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import LinearProgress from '@mui/material/LinearProgress';
import { AttachFile, CheckCircle, Instagram, LinkedIn, WhatsApp } from '@mui/icons-material';
import {
  openFinishFormalizationStream,
  openUploadIdentificationDocumentStream,
  StreamUpdate,
  IdentificationDocumentPayload,
} from '../../services/finish-formalization-service';
import styles from './FinishFormalizationPage.module.css';
import { useParams } from 'react-router-dom';

const getFileExtension = (file: File): string => {
  const lastDotIndex = file.name.lastIndexOf('.');
  if (lastDotIndex !== -1 && lastDotIndex < file.name.length - 1) {
    return file.name.substring(lastDotIndex + 1).toLowerCase();
  }
  if (file.type && file.type.includes('/')) {
    const mimeExtension = file.type.split('/').pop();
    if (mimeExtension) {
      return mimeExtension.toLowerCase();
    }
  }
  return 'bin';
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Não foi possível ler o arquivo selecionado.'));
        return;
      }
      const base64Content = reader.result.split(',').pop();
      if (!base64Content) {
        reject(new Error('Não foi possível converter o arquivo em base64.'));
        return;
      }
      resolve(base64Content);
    };
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo selecionado.'));
    reader.readAsDataURL(file);
  });

const buildIdentificationDocumentPayload = async (
  file: File
): Promise<IdentificationDocumentPayload> => {
  const file_base64 = await fileToBase64(file);
  return {
    file_name: file.name,
    file_extension: getFileExtension(file),
    file_base64,
  };
};

const FinishFormalizationPage: React.FC = () => {
  const [isStreaming, setIsStreaming] = useState<boolean>(true);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [streamData, setStreamData] = useState<StreamUpdate | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const streamCompletionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [streamCompletionDelayOver, setStreamCompletionDelayOver] = useState(false);
  const hasScheduledCompletionRef = useRef(false);

  // Modal de progresso do envio de arquivo
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isUploadStreaming, setIsUploadStreaming] = useState(false);
  const [uploadStreamData, setUploadStreamData] = useState<StreamUpdate | null>(null);
  const [uploadStreamNetworkError, setUploadStreamNetworkError] = useState<string | null>(null);

  const whatsappLink = 'https://wa.me/5511988142012';
  const linkedinLink = 'https://www.linkedin.com/company/90832836/admin/page-posts/published/';
  const instagramLink = 'https://www.instagram.com/cisp1.io/';
  const isCompletedSuccessfully = !!(
    streamData &&
    streamData.success &&
    streamData.total_steps === streamData.current_step
  );
  const hideStreamUi = isCompletedSuccessfully && streamCompletionDelayOver;
  const searchTerm = useParams().search_term;

  useEffect(() => {
    startStream();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (streamCompletionTimerRef.current) {
        clearTimeout(streamCompletionTimerRef.current);
        streamCompletionTimerRef.current = null;
      }
    };
  }, []);

  const startStream = async () => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (streamCompletionTimerRef.current) {
        clearTimeout(streamCompletionTimerRef.current);
        streamCompletionTimerRef.current = null;
      }
      hasScheduledCompletionRef.current = false;
      setStreamCompletionDelayOver(false);
      setIsStreaming(true);
      setNetworkError(null);
      setStreamData(null);
      setUploadError(null);

      const { controller, promise } = openFinishFormalizationStream(
        (searchTerm as string) || '',
        obj => {
          setStreamData(prev => {
            const merged = { ...prev, ...obj } as StreamUpdate;
            if (merged.legal_representative_identification_document_already_sent === true) {
              setUploadSuccess(true);
            }
            if (
              merged &&
              merged.success &&
              merged.total_steps === merged.current_step &&
              !hasScheduledCompletionRef.current
            ) {
              hasScheduledCompletionRef.current = true;
              streamCompletionTimerRef.current = setTimeout(() => {
                setStreamCompletionDelayOver(true);
              }, 2000);
            }
            return merged;
          });
        }
      );
      abortControllerRef.current = controller;
      await promise;
    } catch (err: any) {
      if (
        err?.name === 'AbortError' ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'))
      ) {
        return;
      }
      console.error('Erro no stream:', err);
      setNetworkError(
        'Houve um erro ao tentar buscar as informações para formalizar sua proposta. Por favor, tente novamente.'
      );
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError(null);
    setUploadSuccess(false);
    setUploadError(null);

    if (!file) {
      return;
    }

    // Validar tipo de arquivo
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Apenas arquivos PDF, JPG e PNG são permitidos.');
      setSelectedFile(null);
      return;
    }

    // Validar tamanho (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError('O arquivo deve ter no máximo 10MB.');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleSendFile = async () => {
    if (!selectedFile) {
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);
      setUploadSuccess(false);

      const payload = await buildIdentificationDocumentPayload(selectedFile);

      // Abrir modal e iniciar stream do upload
      setUploadModalOpen(true);
      await startUploadStream(payload);
    } catch (err: any) {
      console.error('Erro ao enviar arquivo:', err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Erro ao enviar o arquivo. Por favor, tente novamente.';
      setUploadError(errorMessage);
      setUploadSuccess(false);
    } finally {
      setIsUploading(false);
    }
  };

  const startUploadStream = async (payload: IdentificationDocumentPayload) => {
    try {
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
      }
      setIsUploadStreaming(true);
      setUploadStreamNetworkError(null);
      setUploadStreamData(null);

      const { controller, promise } = openUploadIdentificationDocumentStream(
        (searchTerm as string) || '',
        payload,
        obj => {
          setUploadStreamData(prev => {
            const merged = { ...prev, ...obj } as StreamUpdate;
            if (merged.success && merged.total_steps === merged.current_step) {
              setUploadSuccess(true);
              setTimeout(() => {
                setUploadModalOpen(false);
              }, 2000);
              controller.abort();
            }
            return merged;
          });
        }
      );
      uploadAbortControllerRef.current = controller;
      await promise;
    } catch (err: any) {
      if (
        err?.name === 'AbortError' ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('aborted'))
      ) {
        return;
      }
      console.error('Erro no stream de upload:', err);
      setUploadStreamNetworkError('Houve um erro durante o envio. Tente novamente.');
    } finally {
      setIsUploadStreaming(false);
      uploadAbortControllerRef.current = null;
    }
  };

  const retryUploadStream = async () => {
    if (!selectedFile) return;
    try {
      const payload = await buildIdentificationDocumentPayload(selectedFile);
      await startUploadStream(payload);
    } catch (error) {
      console.error('Erro ao preparar arquivo para reenvio:', error);
      setUploadStreamNetworkError(
        'Não foi possível preparar o arquivo para reenvio. Selecione o arquivo novamente.'
      );
    }
  };

  const handleCloseUploadModal = () => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
    }
    setUploadModalOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Formulário enviado', { selectedFile });
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box
        sx={{
          backgroundColor: '#EC0000',
          color: 'white',
        }}
      >
        <Box
          maxWidth={960}
          mx="auto"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          py={1.5}
          px={2}
        >
          <Typography variant="h6" component="div" margin={{ xs: 'auto', sm: 0 }} fontWeight={600}>
            Formalização da Proposta
          </Typography>
          <img src="/images/cisp1-white-logo.png" alt="CISP1" className={styles.headerLogo} />
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" height="calc(100vh - 144px)" bgcolor="#f0f0f0">
        <Box
          display="flex"
          maxWidth={600}
          mx="auto"
          width="100%"
          px={2}
          py={{
            xs: 3,
            md: 4,
          }}
          margin="auto"
        >
          <Box
            padding={{
              xs: 2.5,
              md: 4,
            }}
            borderRadius={2}
            boxShadow="0 8px 24px rgba(0,0,0,0.08)"
            margin="auto"
            bgcolor="#ffffff"
          >
            {networkError ? (
              <Box display="flex" flexDirection="column" gap={2} padding={2} textAlign="center">
                <Alert severity="error">{networkError}</Alert>
                <Button
                  variant="contained"
                  onClick={startStream}
                  sx={{
                    backgroundColor: '#EC0000 !important',
                    color: 'white !important',
                  }}
                >
                  Tentar novamente
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  marginBottom={2}
                  textAlign={{
                    xs: 'center',
                    sm: 'left',
                  }}
                >
                  <Typography variant="h6" component="h1" fontWeight={600}>
                    Finalização da Formalização
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Acompanhando o progresso para finalizar sua proposta.
                  </Typography>
                </Box>

                {streamData && !hideStreamUi && (
                  <Box display="flex" flexDirection="column" gap={1.5} mb={2.5}>
                    <Typography variant="body1">{streamData.current_step_description}</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box flex={1}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            streamData.total_steps && streamData.total_steps > 0
                              ? Math.min(
                                  100,
                                  Math.round(
                                    (streamData.current_step / streamData.total_steps) * 100
                                  )
                                )
                              : 0
                          }
                          sx={{
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#EC0000',
                            },
                            backgroundColor: '#F8D7DA',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        minWidth={42}
                        textAlign="right"
                      >
                        {streamData.total_steps && streamData.total_steps > 0
                          ? `${Math.min(100, Math.round((streamData.current_step / streamData.total_steps) * 100))}%`
                          : '0%'}
                      </Typography>
                    </Box>
                    {!streamData.success && (
                      <Box mt={1}>
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          {streamData.current_step_description}
                        </Alert>
                        <Button
                          variant="contained"
                          onClick={startStream}
                          sx={{
                            backgroundColor: '#EC0000 !important',
                            color: 'white !important',
                          }}
                        >
                          Tentar novamente
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {isStreaming && !streamData && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    padding={2}
                    textAlign="center"
                  >
                    <Typography variant="body1">Carregando...</Typography>
                  </Box>
                )}

                <Box display="flex" flexDirection="column" gap={2.5}>
                  {streamData &&
                    streamData.success &&
                    streamData.total_steps === streamData.current_step &&
                    streamCompletionDelayOver && (
                      <>
                        {streamData.qrcode_base64_image && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 1,
                              border: '1px dashed #E0E0E0',
                              borderRadius: 1.5,
                              p: 2.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              QR Code
                            </Typography>
                            <img
                              src={`data:image/png;base64,${streamData.qrcode_base64_image}`}
                              alt="QR Code"
                              className={styles.qrcodeImage}
                            />
                          </Box>
                        )}

                        {streamData.legal_representative_identification_document_already_sent ===
                          false && (
                          <>
                            <Box>
                              <input
                                accept=".pdf,.jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                onChange={handleFileChange}
                                disabled={uploadSuccess}
                              />
                              <label htmlFor="file-upload" className={styles.fileUploadLabel}>
                                <Button
                                  variant="outlined"
                                  component="span"
                                  fullWidth
                                  disabled={uploadSuccess}
                                  startIcon={
                                    uploadSuccess ? (
                                      <CheckCircle sx={{ color: 'success.main' }} />
                                    ) : (
                                      <AttachFile sx={{ color: '#EC0000' }} />
                                    )
                                  }
                                  sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    py: 1.5,
                                    borderColor: uploadSuccess ? 'success.main' : '#E0E0E0',
                                    color: 'text.primary',
                                    '&:hover': {
                                      borderColor: '#EC0000',
                                      backgroundColor: 'rgba(236, 0, 0, 0.04)',
                                    },
                                    height: '48px',
                                  }}
                                >
                                  {uploadSuccess
                                    ? 'Documento enviado com sucesso'
                                    : selectedFile
                                      ? selectedFile.name
                                      : 'Documento de Identificação do Representante Legal'}
                                </Button>
                              </label>
                              {fileError && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  {fileError}
                                </Typography>
                              )}
                              {selectedFile && !fileError && !uploadSuccess && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  Arquivo selecionado: {selectedFile.name} (
                                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={handleSendFile}
                              disabled={!selectedFile || isUploading || uploadSuccess}
                              className={styles.sendButton}
                              sx={{
                                backgroundColor: '#EC0000 !important',
                                color: 'white !important',
                                '&:hover': {
                                  backgroundColor: 'rgba(236, 0, 0, 0.9) !important',
                                },
                                '&:disabled': {
                                  opacity: 0.6,
                                  backgroundColor: '#D0D5DD !important',
                                },
                              }}
                            >
                              {isUploading ? (
                                <>
                                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                  Enviando...
                                </>
                              ) : (
                                'Enviar Arquivo'
                              )}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                </Box>
              </form>
            )}
          </Box>
        </Box>
      </Box>

      {/* Modal de progresso do envio de arquivo */}
      <Dialog open={uploadModalOpen} onClose={handleCloseUploadModal} maxWidth="sm" fullWidth>
        <DialogTitle>Enviando documento</DialogTitle>
        <DialogContent>
          {uploadStreamNetworkError && (
            <Box display="flex" flexDirection="column" gap={2} my={1.5}>
              <Alert severity="error">{uploadStreamNetworkError}</Alert>
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={1.5}
                width="100%"
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseUploadModal}
                  sx={{
                    borderColor: '#EC0000',
                    color: '#EC0000',
                    backgroundColor: '#FFFFFF',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#EC0000',
                      backgroundColor: 'rgba(236, 0, 0, 0.04)',
                    },
                    flex: 1,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={retryUploadStream}
                  sx={{
                    backgroundColor: '#EC0000 !important',
                    color: 'white !important',
                    flex: 1,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Tentar novamente
                </Button>
              </Box>
            </Box>
          )}

          {!uploadStreamNetworkError && (
            <Box display="flex" flexDirection="column" gap={1.5} my={1}>
              {uploadStreamData ? (
                <>
                  <Typography variant="body1">
                    {uploadStreamData.current_step_description}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box flex={1}>
                      <LinearProgress
                        variant="determinate"
                        value={
                          uploadStreamData.total_steps && uploadStreamData.total_steps > 0
                            ? Math.min(
                                100,
                                Math.round(
                                  (uploadStreamData.current_step / uploadStreamData.total_steps) *
                                    100
                                )
                              )
                            : 0
                        }
                        sx={{
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#EC0000',
                          },
                          backgroundColor: '#F8D7DA',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      minWidth={42}
                      textAlign="right"
                    >
                      {uploadStreamData.total_steps && uploadStreamData.total_steps > 0
                        ? `${Math.min(100, Math.round((uploadStreamData.current_step / uploadStreamData.total_steps) * 100))}%`
                        : '0%'}
                    </Typography>
                  </Box>
                  {!uploadStreamData.success && (
                    <Box mt={1}>
                      <Alert severity="error" sx={{ mb: 1.5 }}>
                        {uploadStreamData.current_step_description}
                      </Alert>
                      <Box
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        gap={1.5}
                        width="100%"
                      >
                        <Button
                          variant="outlined"
                          onClick={handleCloseUploadModal}
                          sx={{
                            borderColor: '#EC0000',
                            color: '#EC0000',
                            backgroundColor: '#FFFFFF',
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#EC0000',
                              backgroundColor: 'rgba(236, 0, 0, 0.04)',
                            },
                            flex: 1,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          Cancelar
                        </Button>
                        <Button
                          variant="contained"
                          onClick={retryUploadStream}
                          sx={{
                            backgroundColor: '#EC0000 !important',
                            color: 'white !important',
                            flex: 1,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          Tentar novamente
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  padding={2}
                  textAlign="center"
                >
                  <Typography variant="body1">Preparando envio...</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Box
        bgcolor="#FFFFFF"
        display="flex"
        alignItems="center"
        padding={{
          xs: 2,
          sm: 3,
        }}
      >
        <Box
          sx={{
            maxWidth: 960,
            mx: 'auto',
            width: '100%',
            px: 2,
            py: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src="/images/cisp1-logo.png" alt="CISP1" style={{ height: 28 }} />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              aria-label="WhatsApp"
              sx={{ color: '#EC0000' }}
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsApp />
            </IconButton>
            <IconButton
              aria-label="LinkedIn"
              sx={{ color: '#EC0000' }}
              href={linkedinLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedIn />
            </IconButton>
            <IconButton
              aria-label="Instagram"
              sx={{ color: '#EC0000' }}
              href={instagramLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Instagram />
            </IconButton>
          </Box>

          <Button
            variant="outlined"
            sx={{
              borderColor: '#EC0000',
              color: '#EC0000',
              backgroundColor: '#FFFFFF',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#EC0000',
                backgroundColor: '#EC0000',
                color: '#FFFFFF',
              },
            }}
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Fale com nossos consultores
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FinishFormalizationPage;
